import { useState, useEffect, useRef } from 'react';

export type SSETaskStatus = 'idle' | 'processing' | 'extracting' | 'completed' | 'failed';

export interface ProgressState {
  progress: number;
  statusText: string;
}

const STATUS_CONFIG: Record<SSETaskStatus, { progress: number; text: string }> = {
  idle: { progress: 0, text: 'Connecting to Clover...' },
  processing: { progress: 30, text: 'Processing your menu...' },
  extracting: { progress: 70, text: 'Extracting menu items...' },
  completed: { progress: 100, text: 'All done!' },
  failed: { progress: 0, text: 'Something went wrong' },
};

/**
 * Custom hook for smooth progress animation based on SSE task status
 * 
 * @param status - Current SSE task status
 * @returns Object containing progress percentage and status text
 * 
 * @example
 * const { progress, statusText } = useProgressAnimation(status);
 * // progress: 0-100
 * // statusText: "Processing your menu..."
 */
export const useProgressAnimation = (status: SSETaskStatus): ProgressState => {
  const [progress, setProgress] = useState(0);
  const [statusText, setStatusText] = useState(STATUS_CONFIG.idle.text);

  const animationRef = useRef<number | null>(null);
  const progressRef = useRef(0);

  // Keep ref synced with state to avoid stale closures
  useEffect(() => {
    progressRef.current = progress;
  }, [progress]);

  useEffect(() => {
    const config = STATUS_CONFIG[status];
    let targetProgress = config.progress;

    // Prevent backward animation except for failed state (where reset is expected)
    if (status !== 'failed') {
      targetProgress = Math.max(progressRef.current, targetProgress);
    }

    // Update status text immediately
    setStatusText(config.text);

    // Cancel any ongoing animation
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    const startProgress = progressRef.current;
    const duration = 1500; // 1.5 seconds
    const startTime = performance.now(); // More precise than Date.now()

    // Skip tiny animations (< 2% difference)
    if (Math.abs(targetProgress - startProgress) < 2) {
      setProgress(targetProgress);
      return;
    }

    const animate = () => {
      const elapsed = performance.now() - startTime;
      const ratio = Math.min(elapsed / duration, 1);

      // Ease-in-out function for smooth animation
      const eased = ratio < 0.5
        ? 2 * ratio * ratio
        : 1 - Math.pow(-2 * ratio + 2, 2) / 2;

      const currentProgress = startProgress + (targetProgress - startProgress) * eased;
      setProgress(Math.round(currentProgress));

      if (ratio < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        animationRef.current = null;
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    // Cleanup on unmount or status change
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [status]);

  return { progress, statusText };
};
